import {lazy} from 'react';

import dayjs from 'dayjs';
import {PreviewSuspense} from 'next-sanity/preview';
import {useRouter} from 'next/router';

import {BlogPost} from 'components/BlogPost';
import {getArticleSlug, isArticleDateValid} from 'utils/blog';
import {getSinglePost, getSinglePostAsSlugOnly} from 'utils/sanity.client';
import {urlFor} from 'utils/sanity.image';

const BlogPostPreview = lazy(async () => await import('components/BlogPostPreview'));

const Post = ({post, preview, slugWithoutDates}) => {
  const router = useRouter();

  // If the page is not yet generated, this will be displayed
  // initially until getStaticProps() finishes running
  if (router.isFallback) {
    return <div>Loading...</div>;
  }

  if (!post && preview === false) {
    // Handling an edge case where this page is prerendered with undefined props
    // during the build.
    return null;
  }

  const articleDateFromSlug = (router.query.slug as string[])?.slice(0, 3).join('/');

  if (!preview && !isArticleDateValid(articleDateFromSlug, post.publishedAt)) {
    return null;
  }

  const slugAsQueryParam = {
    slugWithoutDates,
  };

  if (preview) {
    return (
      <PreviewSuspense fallback="Loading...">
        <BlogPostPreview queryParams={slugAsQueryParam} />
      </PreviewSuspense>
    );
  } else {
    const meta = {
      title: post.title,
      description: post.description,
      date: dayjs(post.publishedAt).format('MMMM D, YYYY'),
      tags: post.tags,
      authors: post.authors,
      authorLinks: post.authorLinks.map(author => author.current),
      image: !post.mainImage?.asset?._ref ? null : urlFor(post.mainImage.asset._ref).url(),
    };

    return <BlogPost meta={meta} post={post} isMarkdown={false} />;
  }
};

export async function getStaticPaths() {
  const posts = await getSinglePostAsSlugOnly();

  const paths = posts.map(post => ({
    params: {slug: [getArticleSlug(post.slug, post.publishedAt)]},
  }));

  return {
    paths,
    fallback: true,
  };
}

export async function getStaticProps({params, preview = false}) {
  // It's important to default the slug so that it doesn't return "undefined"
  const {slug = ''} = params;

  // runtime ['2023', '04', '17', 'polar-signals-at-kubecon-eu-2023']
  // buildtime [ '/posts/2022/06/23/how-we-use-gRPC-to-build-our-frontend' ]

  // This right here was an interesting one. The slug is different at build time vs runtime.
  // At build time, the slug is a string, and at runtime, it's an array. So to be able to get the slug without the dates e.g. "how-we-use-gRPC-to-build-our-frontend", we need to check if the slug is an array or a string.
  // If it's an array, we need to split it and get the 5th element. If it's a string, we can just return the slug.

  let slugAtBuildTime;

  if (slug.length === 1 && slug[0].includes('/')) {
    slugAtBuildTime = slug[0].split('/')[5];
  }

  const slugAtRuntime = slug[3];

  const slugWithoutDates =
    slug.length === 1 && slug[0].includes('/') ? slugAtBuildTime : slugAtRuntime;

  if (preview) {
    return {
      props: {
        preview,
        slugWithoutDates,
      },
    };
  }

  const post = await getSinglePost(slugWithoutDates);

  return {
    props: {
      preview,
      post,
      slugWithoutDates,
    },
    revalidate: 60,
  };
}

export default Post;
